import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, ActivityIndicator, Linking, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { observer } from '@legendapp/state/react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import theme from '@/config/theme';
import { APP_CONSTANTS } from '@/config/constants';
import { supabase } from '@/services/supabase';
import { globalState } from '@/state/globalState';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { useWebAlert } from '@/components/WebAlert';
import { DevPanelLogger } from '@/utils/logger';

type DevPanelData = {
  companies: any[];
  users: any[];
  checklists: any[];
  subscriptions: any[];
  plans: any[];
  payments: any[];
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['ativa', 'cancelamento_agendado']);
const PENDING_PAYMENT_STATUSES = new Set(['pending', 'failed']);
const TRIAL_EXTERNAL_PREFIX = 'manual:trial-';
const DEV_PANEL_PAGE_SIZE = APP_CONSTANTS.SYNC.SUPABASE_PULL_PAGE_SIZE ?? 1000;

const logger = DevPanelLogger.Main;
const dataLogger = DevPanelLogger.Data;

type PagedFetchResult<T> = {
  data: T[];
  error: any;
  pages: number;
  elapsedMs: number;
};

const fetchAllPages = async <T,>(
  label: string,
  queryBuilder: (from: number, to: number) => any,
  pageSize: number = DEV_PANEL_PAGE_SIZE,
): Promise<PagedFetchResult<T>> => {
  const startTime = Date.now();
  let page = 0;
  let pagesFetched = 0;
  const allRows: T[] = [];

  while (true) {
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data: pageData, error } = await queryBuilder(from, to);
    pagesFetched += 1;

    if (error) {
      return {
        data: [],
        error,
        pages: pagesFetched,
        elapsedMs: Date.now() - startTime,
      };
    }

    const normalized = (pageData ?? []) as T[];
    allRows.push(...normalized);

    if (normalized.length < pageSize) {
      break;
    }

    page += 1;
  }

  return {
    data: allRows,
    error: null,
    pages: pagesFetched,
    elapsedMs: Date.now() - startTime,
  };
};

const normalizeTimestamp = (value?: string | number | null): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      return asNumber < 1e12 ? asNumber * 1000 : asNumber;
    }
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDate = (value?: string | number | null): string => {
  const normalized = normalizeTimestamp(value);
  if (!normalized) return '—';
  return new Date(normalized).toLocaleDateString('pt-BR');
};

const formatCurrency = (value: number): string => {
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  } catch {
    return `R$ ${value.toFixed(2)}`;
  }
};

const resolvePaymentLabel = (status?: string | null): string => {
  if (!status) return 'Desconhecido';
  const normalized = status.toLowerCase();
  if (normalized === 'paid') return 'Pago';
  if (normalized === 'pending') return 'Pendente';
  if (normalized === 'failed') return 'Falhou';
  return status;
};

const resolveSubscriptionLabel = (status?: string | null): string => {
  if (!status) return 'Inativa';
  const normalized = status.toLowerCase();
  if (normalized === 'ativa') return 'Ativa';
  if (normalized === 'cancelamento_agendado') return 'Cancelamento agendado';
  if (normalized === 'cancelada') return 'Cancelada';
  if (normalized === 'trial') return 'Trial';
  if (normalized === 'inativa') return 'Inativa';
  return status;
};

const resolveBillingProvider = (externalId?: string | null): string => {
  if (!externalId) return '—';
  if (externalId.startsWith('manual:apple:')) return 'App Store';
  return externalId.startsWith('manual:') ? 'PIX manual' : 'Stripe';
};

const normalizePhoneToWhatsapp = (value?: string | null): string | null => {
  if (!value) return null;
  const digits = value.replace(/\D+/g, '');
  if (!digits) return null;
  return digits.startsWith('55') ? digits : `55${digits}`;
};

const buildWhatsappUrl = (value?: string | null): string | null => {
  const normalized = normalizePhoneToWhatsapp(value);
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
};

const sortCompaniesBySubscription = (a: any, b: any) => {
  const rank = (status: string) => (status === 'ativa' ? 0 : status === 'trial' ? 1 : 2);
  const diff = rank(a.subscriptionStatus) - rank(b.subscriptionStatus);
  if (diff !== 0) return diff;
  const resolveExpiry = (company: any) => {
    if (company.subscriptionStatus === 'ativa') {
      return normalizeTimestamp(company.nextBillingDate) ?? 0;
    }
    if (company.subscriptionStatus === 'trial') {
      return normalizeTimestamp(company.trialEndDate) ?? 0;
    }
    return normalizeTimestamp(company.lastPaidEndDate)
      ?? normalizeTimestamp(company.lastTrialEndDate)
      ?? 0;
  };
  const expiryDiff = resolveExpiry(b) - resolveExpiry(a);
  if (expiryDiff !== 0) return expiryDiff;
  return a.nome?.localeCompare(b.nome ?? '') ?? 0;
};

const StatusPill = ({ label, tone }: { label: string; tone: 'success' | 'warning' | 'error' | 'neutral' }) => {
  const toneColor = {
    success: theme.colors.util.success,
    warning: theme.colors.util.warning,
    error: theme.colors.util.error,
    neutral: theme.colors.text.tertiary,
  }[tone];
  return (
    <View style={[styles.pill, { borderColor: toneColor, backgroundColor: `${toneColor}22` }]}>
      <Text style={[styles.pillText, { color: toneColor }]}>{label}</Text>
    </View>
  );
};

const KpiCard = ({
  title,
  value,
  subtitle,
  colors = theme.colors.primary.gradient as [string, string],
}: {
  title: string;
  value: string;
  subtitle?: string;
  colors?: [string, string];
}) => (
  <LinearGradient colors={colors} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.kpiCard}>
    <Text style={styles.kpiTitle}>{title}</Text>
    <Text style={styles.kpiValue}>{value}</Text>
    {subtitle ? <Text style={styles.kpiSubtitle}>{subtitle}</Text> : null}
  </LinearGradient>
);

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <View style={styles.sectionCard}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

const DevPanelScreen = observer(() => {
  const router = useRouter();
  const { showAlert } = useWebAlert();
  const userProfile = globalState.user.profile.get();
  const isDeveloper = hasDeveloperBypass(userProfile?.user_type);
  const isConnected = globalState.network.isConnected.get();

  const [activeTab, setActiveTab] = useState<'overview' | 'companies' | 'finance' | 'devops'>('overview');
  const [data, setData] = useState<DevPanelData>({
    companies: [],
    users: [],
    checklists: [],
    subscriptions: [],
    plans: [],
    payments: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedMotoCompanies, setSelectedMotoCompanies] = useState<string[]>([]);
  const [isProvisioningMoto, setIsProvisioningMoto] = useState(false);
  const [provisionMotoMessage, setProvisionMotoMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isDeveloper) {
      const message = 'Acesso restrito: somente usuarios com perfil de desenvolvedor podem abrir este painel.';
      if (Platform.OS !== 'android') {
        showAlert('Acesso negado', message, [{ text: 'Voltar', onPress: () => router.back() }]);
      } else {
        Alert.alert('Acesso negado', message, [{ text: 'OK', onPress: () => router.back() }]);
      }
    }
  }, [isDeveloper, router, showAlert]);

  const loadData = useCallback(async () => {
    if (!isDeveloper) return;
    if (!isConnected) {
      setError('Sem conexao. Este painel funciona apenas online.');
      return;
    }

    logger.info('[painel-dev] Iniciando carregamento de dados');
    setIsLoading(true);
    setError(null);
    const T = APP_CONSTANTS.DATABASE.TABLES;
    try {
      const loadStart = Date.now();
      const [
        companiesRes,
        usersRes,
        checklistsRes,
        subscriptionsRes,
        plansRes,
        paymentsRes,
      ] = await Promise.all([
        fetchAllPages(
          'empresas',
          (from, to) => supabase
            .from(T.EMPRESAS.NAME)
            .select('id, nome, cnpj, plano, num_fotos, stripe_customer_id, email, telefone, created_at, updated_at')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages(
          'usuarios',
          (from, to) => supabase
            .from(T.USERS.NAME)
            .select('id, empresa_id')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages(
          'checklists',
          (from, to) => supabase
            .from(T.GUINCHO_CHECKLISTS.NAME)
            .select('id, empresa_id, status, data_criacao, data_conclusao, created_at, updated_at')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages(
          'assinaturas',
          (from, to) => supabase
            .from(T.SUBSCRIPTIONS.NAME)
            .select('id, empresa_id, status, subscription_plan_id, start_date, end_date, next_billing_date, external_subscription_id, created_at, updated_at')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages(
          'planos',
          (from, to) => supabase
            .from(T.SUBSCRIPTION_PLANS.NAME)
            .select('id, name, price')
            .order('id', { ascending: true })
            .range(from, to)
        ),
        fetchAllPages(
          'pagamentos',
          (from, to) => supabase
            .from(T.PAYMENTS.NAME)
            .select('id, empresa_id, amount, status, payment_date, created_at')
            .order('created_at', { ascending: false })
            .range(from, to)
        ),
      ]);

      const errors: string[] = [];
      if (companiesRes.error) errors.push('empresas');
      if (usersRes.error) errors.push('usuarios');
      if (checklistsRes.error) errors.push('checklists');
      if (subscriptionsRes.error) errors.push('assinaturas');
      if (plansRes.error) errors.push('planos');
      if (paymentsRes.error) errors.push('pagamentos');

      if (companiesRes.error) dataLogger.error('[painel-dev] Erro ao carregar empresas:', companiesRes.error);
      if (usersRes.error) dataLogger.error('[painel-dev] Erro ao carregar usuarios:', usersRes.error);
      if (checklistsRes.error) dataLogger.error('[painel-dev] Erro ao carregar checklists:', checklistsRes.error);
      if (subscriptionsRes.error) dataLogger.error('[painel-dev] Erro ao carregar assinaturas:', subscriptionsRes.error);
      if (plansRes.error) dataLogger.error('[painel-dev] Erro ao carregar planos:', plansRes.error);
      if (paymentsRes.error) dataLogger.error('[painel-dev] Erro ao carregar pagamentos:', paymentsRes.error);

      setData({
        companies: companiesRes.data ?? [],
        users: usersRes.data ?? [],
        checklists: checklistsRes.data ?? [],
        subscriptions: subscriptionsRes.data ?? [],
        plans: plansRes.data ?? [],
        payments: paymentsRes.data ?? [],
      });

      if (errors.length > 0) {
        setError(`Falha ao carregar: ${errors.join(', ')}.`);
      }

      setLastUpdated(new Date());

      dataLogger.info(`[painel-dev] Empresas: ${companiesRes.data.length} itens | ${companiesRes.pages} paginas | ${companiesRes.elapsedMs}ms`);
      dataLogger.info(`[painel-dev] Usuarios: ${usersRes.data.length} itens | ${usersRes.pages} paginas | ${usersRes.elapsedMs}ms`);
      dataLogger.info(`[painel-dev] Checklists: ${checklistsRes.data.length} itens | ${checklistsRes.pages} paginas | ${checklistsRes.elapsedMs}ms`);
      dataLogger.info(`[painel-dev] Assinaturas: ${subscriptionsRes.data.length} itens | ${subscriptionsRes.pages} paginas | ${subscriptionsRes.elapsedMs}ms`);
      dataLogger.info(`[painel-dev] Planos: ${plansRes.data.length} itens | ${plansRes.pages} paginas | ${plansRes.elapsedMs}ms`);
      dataLogger.info(`[painel-dev] Pagamentos: ${paymentsRes.data.length} itens | ${paymentsRes.pages} paginas | ${paymentsRes.elapsedMs}ms`);

      const totalElapsed = Date.now() - loadStart;
      dataLogger.info(
        `[painel-dev] Resumo: empresas=${companiesRes.data.length} usuarios=${usersRes.data.length} checklists=${checklistsRes.data.length} assinaturas=${subscriptionsRes.data.length} planos=${plansRes.data.length} pagamentos=${paymentsRes.data.length} | total=${totalElapsed}ms | pageSize=${DEV_PANEL_PAGE_SIZE}`
      );
    } catch (err) {
      setError('Erro inesperado ao carregar dados do Supabase.');
      logger.error('[painel-dev] Erro inesperado ao carregar dados do Supabase:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, isDeveloper]);

  useEffect(() => {
    if (isDeveloper && isConnected) {
      loadData();
    }
  }, [isDeveloper, isConnected, loadData]);

  const plansById = useMemo(() => {
    const map = new Map<string, any>();
    data.plans.forEach(plan => {
      if (plan?.id) map.set(plan.id, plan);
    });
    return map;
  }, [data.plans]);

  const subscriptionsByEmpresa = useMemo(() => {
    const map = new Map<string, any[]>();
    data.subscriptions.forEach(sub => {
      if (!sub?.empresa_id) return;
      const list = map.get(sub.empresa_id) ?? [];
      list.push(sub);
      map.set(sub.empresa_id, list);
    });
    return map;
  }, [data.subscriptions]);

  const usersByEmpresa = useMemo(() => {
    const map = new Map<string, number>();
    data.users.forEach(user => {
      if (!user?.empresa_id) return;
      map.set(user.empresa_id, (map.get(user.empresa_id) ?? 0) + 1);
    });
    return map;
  }, [data.users]);

  const checklistsByEmpresa = useMemo(() => {
    const map = new Map<string, number>();
    data.checklists.forEach(checklist => {
      if (!checklist?.empresa_id) return;
      map.set(checklist.empresa_id, (map.get(checklist.empresa_id) ?? 0) + 1);
    });
    return map;
  }, [data.checklists]);

  const lastActivityByEmpresa = useMemo(() => {
    const map = new Map<string, number>();
    data.checklists.forEach(checklist => {
      if (!checklist?.empresa_id) return;
      const lastTs = normalizeTimestamp(checklist.updated_at)
        ?? normalizeTimestamp(checklist.data_conclusao)
        ?? normalizeTimestamp(checklist.data_criacao)
        ?? normalizeTimestamp(checklist.created_at);
      if (!lastTs) return;
      const current = map.get(checklist.empresa_id) ?? 0;
      if (lastTs > current) map.set(checklist.empresa_id, lastTs);
    });
    return map;
  }, [data.checklists]);

  const companiesWithMetrics = useMemo(() => {
    const now = Date.now();
    return data.companies.map(company => {
      const subscriptions = subscriptionsByEmpresa.get(company.id) ?? [];
      const paidSubscriptions = subscriptions.filter(sub => sub?.external_subscription_id && !sub.external_subscription_id.startsWith(TRIAL_EXTERNAL_PREFIX));
      const trialSubscriptions = subscriptions.filter(sub => sub?.external_subscription_id && sub.external_subscription_id.startsWith(TRIAL_EXTERNAL_PREFIX));

      const resolveValidThrough = (sub: any): number | null => {
        const nextBilling = normalizeTimestamp(sub?.next_billing_date);
        const endDate = normalizeTimestamp(sub?.end_date);
        return Math.max(nextBilling ?? 0, endDate ?? 0) || null;
      };

      const resolveActivePaid = (): any | null => {
        const active = paidSubscriptions.filter(sub => {
          if (!ACTIVE_SUBSCRIPTION_STATUSES.has((sub?.status ?? '').toLowerCase())) return false;
          const validThrough = resolveValidThrough(sub);
          return !!validThrough && validThrough >= now;
        });
        if (active.length === 0) return null;
        active.sort((a, b) => (resolveValidThrough(b) ?? 0) - (resolveValidThrough(a) ?? 0));
        return active[0];
      };

      const resolveActiveTrial = (): any | null => {
        const active = trialSubscriptions.filter(sub => {
          if (!ACTIVE_SUBSCRIPTION_STATUSES.has((sub?.status ?? '').toLowerCase())) return false;
          const trialEnd = normalizeTimestamp(sub?.end_date);
          return !!trialEnd && trialEnd >= now;
        });
        if (active.length === 0) return null;
        active.sort((a, b) => (normalizeTimestamp(b?.end_date) ?? 0) - (normalizeTimestamp(a?.end_date) ?? 0));
        return active[0];
      };

      const activePaid = resolveActivePaid();
      const activeTrial = resolveActiveTrial();
      const lastPaid = paidSubscriptions.length > 0
        ? paidSubscriptions.sort((a, b) => (resolveValidThrough(b) ?? 0) - (resolveValidThrough(a) ?? 0))[0]
        : null;
      const lastTrial = trialSubscriptions.length > 0
        ? trialSubscriptions.sort((a, b) => (normalizeTimestamp(b?.end_date) ?? 0) - (normalizeTimestamp(a?.end_date) ?? 0))[0]
        : null;

      const primarySubscription = activePaid ?? activeTrial ?? lastPaid ?? lastTrial ?? null;
      const plan = primarySubscription?.subscription_plan_id ? plansById.get(primarySubscription.subscription_plan_id) : null;
      const planPrice = Number(plan?.price ?? 0);

      const status = activePaid
        ? 'ativa'
        : activeTrial
          ? 'trial'
          : 'inativa';

      return {
        ...company,
        planName: plan?.name ?? company.plano ?? 'Sem plano',
        planPrice: Number.isFinite(planPrice) ? planPrice : 0,
        subscriptionStatus: status,
        subscriptionRawStatus: primarySubscription?.status ?? null,
        nextBillingDate: activePaid ? (activePaid.next_billing_date ?? activePaid.end_date ?? null) : null,
        trialStartDate: activeTrial?.start_date ?? null,
        trialEndDate: activeTrial?.end_date ?? null,
        lastTrialEndDate: lastTrial?.end_date ?? null,
        lastPaidEndDate: lastPaid ? (lastPaid.next_billing_date ?? lastPaid.end_date ?? null) : null,
        billingProvider: resolveBillingProvider(primarySubscription?.external_subscription_id ?? null),
        usersCount: usersByEmpresa.get(company.id) ?? 0,
        checklistsCount: checklistsByEmpresa.get(company.id) ?? 0,
        lastActivity: lastActivityByEmpresa.get(company.id) ?? null,
        hasPaidSubscription: !!activePaid,
        hasTrialSubscription: !!activeTrial,
      };
    });
  }, [data.companies, plansById, subscriptionsByEmpresa, usersByEmpresa, checklistsByEmpresa, lastActivityByEmpresa]);

  const activeSubscriptions = useMemo(
    () => companiesWithMetrics.filter(company => company.hasPaidSubscription),
    [companiesWithMetrics]
  );

  const lastPaidPaymentByEmpresa = useMemo(() => {
    const map = new Map<string, { amount: number; paymentDate: number | null }>();
    data.payments.forEach(payment => {
      const empresaId = payment?.empresa_id;
      if (!empresaId) return;
      const status = (payment?.status ?? '').toLowerCase();
      if (status !== 'paid') return;
      const paymentDate =
        normalizeTimestamp(payment.payment_date)
        ?? normalizeTimestamp(payment.created_at)
        ?? null;
      const current = map.get(empresaId);
      const currentDate = current?.paymentDate ?? 0;
      const nextDate = paymentDate ?? 0;
      if (current && nextDate <= currentDate) return;
      const amount = Number(payment?.amount ?? 0);
      map.set(empresaId, {
        amount: Number.isFinite(amount) ? amount : 0,
        paymentDate,
      });
    });
    return map;
  }, [data.payments]);

  const mrrTotal = useMemo(() => {
    return activeSubscriptions.reduce((total, company) => {
      const lastPayment = lastPaidPaymentByEmpresa.get(company.id);
      const amount = Number(lastPayment?.amount ?? 0);
      return total + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [activeSubscriptions, lastPaidPaymentByEmpresa]);

  const activeCompaniesCount = useMemo(() => {
    return companiesWithMetrics.filter(company => company.subscriptionStatus === 'ativa' || company.subscriptionStatus === 'trial').length;
  }, [companiesWithMetrics]);

  const totalChecklists30d = useMemo(() => {
    const now = Date.now();
    const windowMs = 30 * 24 * 60 * 60 * 1000;
    return data.checklists.filter(checklist => {
      const ts = normalizeTimestamp(checklist.data_criacao)
        ?? normalizeTimestamp(checklist.created_at)
        ?? normalizeTimestamp(checklist.updated_at);
      return ts ? ts >= now - windowMs : false;
    }).length;
  }, [data.checklists]);

  const checklistsInProgress = useMemo(() => {
    return data.checklists.filter(checklist => (checklist?.status ?? '') === 'in_progress').length;
  }, [data.checklists]);

  const activeCompaniesByLastPayment = useMemo(() => {
    return activeSubscriptions
      .map(company => {
        const lastPayment = lastPaidPaymentByEmpresa.get(company.id);
        return {
          ...company,
          lastPaymentAmount: Number(lastPayment?.amount ?? 0),
          lastPaymentDate: lastPayment?.paymentDate ?? null,
        };
      })
      .filter(company => !!company.lastPaymentDate)
      .sort((a, b) => {
        const paymentDateDiff = (b.lastPaymentDate ?? 0) - (a.lastPaymentDate ?? 0);
        if (paymentDateDiff !== 0) return paymentDateDiff;
        const amountDiff = b.lastPaymentAmount - a.lastPaymentAmount;
        if (amountDiff !== 0) return amountDiff;
        return (a.nome ?? '').localeCompare(b.nome ?? '');
      });
  }, [activeSubscriptions, lastPaidPaymentByEmpresa]);

  const topCompaniesByImages = useMemo(() => {
    return [...companiesWithMetrics].sort((a, b) => (b.num_fotos ?? 0) - (a.num_fotos ?? 0)).slice(0, 5);
  }, [companiesWithMetrics]);

  const pendingPayments = useMemo(() => {
    return data.payments.filter(payment => PENDING_PAYMENT_STATUSES.has((payment?.status ?? '').toLowerCase()));
  }, [data.payments]);

  const planDistribution = useMemo(() => {
    const distribution = new Map<string, number>();
    data.subscriptions.forEach(sub => {
      if (sub?.external_subscription_id?.startsWith(TRIAL_EXTERNAL_PREFIX)) return;
      const plan = plansById.get(sub.subscription_plan_id);
      const label = plan?.name ?? 'Sem plano';
      distribution.set(label, (distribution.get(label) ?? 0) + 1);
    });
    return Array.from(distribution.entries()).map(([name, value]) => ({ name, value }));
  }, [data.subscriptions, plansById]);

  const handleRefresh = useCallback(() => {
    loadData();
  }, [loadData]);

  const handleOpenWhatsApp = useCallback((phone?: string | null) => {
    const url = buildWhatsappUrl(phone);
    if (!url) {
      return;
    }
    Linking.openURL(url);
  }, []);

  const sortedCompanies = useMemo(
    () => [...companiesWithMetrics].sort(sortCompaniesBySubscription),
    [companiesWithMetrics],
  );

  const toggleMotoCompany = useCallback((companyId: string) => {
    setSelectedMotoCompanies(prev => (
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    ));
  }, []);

  const handleProvisionMotoTemplates = useCallback(async () => {
    if (!isConnected) {
      setProvisionMotoMessage('Sem conexão para provisionar templates.');
      return;
    }
    if (selectedMotoCompanies.length === 0) {
      const message = 'Selecione pelo menos uma empresa para provisionar o template de motos.';
      if (Platform.OS !== 'android') {
        showAlert('Seleção vazia', message);
      } else {
        Alert.alert('Seleção vazia', message);
      }
      return;
    }

    setIsProvisioningMoto(true);
    setProvisionMotoMessage(null);
    try {
      const { data: response, error: invokeError } = await supabase.functions.invoke(
        'provision-template-moto',
        { body: { empresaIds: selectedMotoCompanies } },
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Falha ao chamar provision-template-moto.');
      }

      const results = response?.results ?? [];
      const created = results.filter((item: any) => item.status === 'created').length;
      const skipped = results.filter((item: any) => item.status === 'skipped').length;
      const failed = results.filter((item: any) => item.status === 'error').length;
      const summary = `Provisionamento finalizado: ${created} criados, ${skipped} ignorados, ${failed} com erro.`;
      setProvisionMotoMessage(summary);
      if (Platform.OS !== 'android') {
        showAlert('Template de motos', summary);
      } else {
        Alert.alert('Template de motos', summary);
      }
    } catch (err: any) {
      const message = err?.message ?? 'Falha ao provisionar templates de motos.';
      setProvisionMotoMessage(message);
      if (Platform.OS !== 'android') {
        showAlert('Erro', message);
      } else {
        Alert.alert('Erro', message);
      }
    } finally {
      setIsProvisioningMoto(false);
    }
  }, [isConnected, selectedMotoCompanies, showAlert]);

  if (!isDeveloper) {
    return null;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Painel Dev</Text>
          <Text style={styles.subtitle}>Dados online via Supabase (sem cache local).</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => router.push('/painel-dev/explorar-checklists')}
          >
            <MaterialCommunityIcons name="clipboard-search-outline" size={18} color={theme.colors.text.primary} />
            <Text style={styles.secondaryButtonText}>Explorar checklists</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.refreshButton} onPress={handleRefresh} disabled={isLoading}>
            {isLoading ? (
              <ActivityIndicator color={theme.colors.text.inverse} />
            ) : (
              <MaterialCommunityIcons name="refresh" size={18} color={theme.colors.text.inverse} />
            )}
            <Text style={styles.refreshText}>Atualizar</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.statusRow}>
        <StatusPill
          label={isConnected ? 'Online' : 'Offline'}
          tone={isConnected ? 'success' : 'error'}
        />
        <Text style={styles.statusText}>
          {lastUpdated ? `Atualizado em ${lastUpdated.toLocaleString('pt-BR')}` : 'Sem sincronizacao recente'}
        </Text>
      </View>

      {error ? (
        <View style={styles.alertCard}>
          <MaterialCommunityIcons name="alert-circle-outline" size={18} color={theme.colors.util.error} />
          <Text style={styles.alertText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.tabRow}>
        {[
          { id: 'overview', label: 'Visao geral', icon: 'view-dashboard-outline' },
          { id: 'companies', label: 'Clientes', icon: 'domain' },
          { id: 'finance', label: 'Assinaturas', icon: 'credit-card-outline' },
          { id: 'devops', label: 'Dev Ops', icon: 'database-outline' },
        ].map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tabButton, isActive && styles.tabButtonActive]}
              onPress={() => setActiveTab(tab.id as any)}
            >
              <MaterialCommunityIcons
                name={tab.icon as any}
                size={16}
                color={isActive ? theme.colors.primary.contrast : theme.colors.text.secondary}
              />
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {activeTab === 'overview' && (
        <View style={styles.tabContent}>
          <View style={styles.kpiGrid}>
            <KpiCard
              title="MRR total"
              value={formatCurrency(mrrTotal)}
              subtitle="Assinaturas ativas"
              colors={[theme.colors.primary.default, theme.colors.primary.light]}
            />
            <KpiCard
              title="Empresas ativas"
              value={String(activeCompaniesCount)}
              subtitle={`De ${data.companies.length} cadastradas`}
              colors={[theme.colors.secondary.default, theme.colors.secondary.light]}
            />
            <KpiCard
              title="Checklists 30d"
              value={String(totalChecklists30d)}
              subtitle="Criados no periodo"
              colors={[theme.colors.util.info, theme.colors.primary.light]}
            />
            <KpiCard
              title="Em andamento"
              value={String(checklistsInProgress)}
              subtitle="Status in_progress"
              colors={[theme.colors.util.warning, theme.colors.secondary.light]}
            />
          </View>

          <SectionCard title="Empresas ativas por ultimo pagamento">
            {activeCompaniesByLastPayment.length === 0 ? (
              <Text style={styles.emptyText}>Nenhuma empresa ativa com pagamento encontrado.</Text>
            ) : (
              activeCompaniesByLastPayment.map(company => (
                <View key={company.id} style={styles.listRow}>
                  <View style={{ flex: 1, paddingRight: theme.spacing.sm }}>
                    <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                    <Text style={styles.listSubtitle}>
                      Ultimo pagamento: {formatDate(company.lastPaymentDate)}
                    </Text>
                  </View>
                  <Text style={styles.listMetric}>{formatCurrency(company.lastPaymentAmount)}</Text>
                </View>
              ))
            )}
          </SectionCard>

        </View>
      )}

      {activeTab === 'companies' && (
        <View style={styles.tabContent}>
          <SectionCard title="Empresas cadastradas">
            {companiesWithMetrics.length === 0 ? (
              <Text style={styles.emptyText}>Nenhuma empresa encontrada.</Text>
            ) : (
              sortedCompanies.map(company => (
                <View key={company.id} style={styles.companyCard}>
                  <View style={styles.companyHeader}>
                    <View>
                      <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                      <Text style={styles.listSubtitle}>{company.cnpj || 'CNPJ nao informado'}</Text>
                    </View>
                    <StatusPill
                      label={resolveSubscriptionLabel(company.subscriptionStatus)}
                      tone={company.subscriptionStatus === 'ativa' ? 'success' : company.subscriptionStatus === 'trial' ? 'warning' : 'neutral'}
                    />
                  </View>
                  <View style={styles.companyMeta}>
                    <Text style={styles.listSubtitle}>Plano: {company.planName}</Text>
                    <Text style={styles.listSubtitle}>Usuarios: {company.usersCount}</Text>
                    <Text style={styles.listSubtitle}>Checklists: {company.checklistsCount}</Text>
                    <Text style={styles.listSubtitle}>Ultima atividade: {formatDate(company.lastActivity)}</Text>
                    {company.subscriptionStatus === 'trial' && (
                      <Text style={styles.listSubtitle}>
                        Trial: {formatDate(company.trialStartDate)} → {formatDate(company.trialEndDate)}
                      </Text>
                    )}
                    {company.subscriptionStatus === 'inativa' && company.lastTrialEndDate && (
                      <Text style={styles.listSubtitle}>
                        Trial encerrado em {formatDate(company.lastTrialEndDate)}
                      </Text>
                    )}
                    {company.subscriptionStatus === 'inativa' && company.lastPaidEndDate && (
                      <Text style={styles.listSubtitle}>
                        Assinatura expirada em {formatDate(company.lastPaidEndDate)}
                      </Text>
                    )}
                  </View>
                </View>
              ))
            )}
          </SectionCard>
        </View>
      )}

      {activeTab === 'finance' && (
        <View style={styles.tabContent}>
          <SectionCard title="Distribuicao de planos">
            {planDistribution.length === 0 ? (
              <Text style={styles.emptyText}>Nenhuma assinatura registrada.</Text>
            ) : (
              planDistribution.map(plan => (
                <View key={plan.name} style={styles.listRow}>
                  <Text style={styles.listTitle}>{plan.name}</Text>
                  <Text style={styles.listMetric}>{plan.value}</Text>
                </View>
              ))
            )}
          </SectionCard>

          <SectionCard title="Pagamentos pendentes ou falhos">
            {pendingPayments.length === 0 ? (
              <Text style={styles.emptyText}>Nenhuma pendencia encontrada.</Text>
            ) : (
              pendingPayments.slice(0, 8).map(payment => (
                <View key={payment.id} style={styles.listRow}>
                  <View>
                    <Text style={styles.listTitle}>{formatCurrency(Number(payment.amount ?? 0))}</Text>
                    <Text style={styles.listSubtitle}>
                      Empresa {payment.empresa_id || '—'} • {formatDate(payment.payment_date)}
                    </Text>
                  </View>
                  <StatusPill
                    label={resolvePaymentLabel(payment.status)}
                    tone={payment.status === 'failed' ? 'error' : 'warning'}
                  />
                </View>
              ))
            )}
          </SectionCard>

          <SectionCard title="Assinaturas por empresa">
            {companiesWithMetrics.length === 0 ? (
              <Text style={styles.emptyText}>Nenhuma assinatura encontrada.</Text>
            ) : (
              sortedCompanies.map(company => (
                <View key={`${company.id}-billing`} style={styles.subscriptionRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                    {company.subscriptionStatus === 'trial' ? (
                      <Text style={styles.listSubtitle}>
                        Trial: {formatDate(company.trialStartDate)} → {formatDate(company.trialEndDate)}
                      </Text>
                    ) : (
                      <>
                        <Text style={styles.listSubtitle}>
                          {company.planName} • {formatCurrency(company.planPrice)}
                        </Text>
                        <Text style={styles.listSubtitle}>
                          Prox. vencimento: {formatDate(company.nextBillingDate)}
                        </Text>
                      </>
                    )}
                    {company.email ? (
                      <Text style={styles.listSubtitle}>
                        Email: {company.email}
                      </Text>
                    ) : null}
                    <Text style={styles.listSubtitle}>
                      Checklists: {company.checklistsCount}
                    </Text>
                    {company.telefone ? (
                      <Text
                        style={styles.listSubtitle}
                        onPress={() => handleOpenWhatsApp(company.telefone)}
                      >
                        WhatsApp: {company.telefone}
                      </Text>
                    ) : (
                      <Text style={styles.listSubtitle}>WhatsApp: —</Text>
                    )}
                    {company.subscriptionStatus === 'inativa' && company.lastTrialEndDate && (
                      <Text style={styles.listSubtitle}>
                        Trial expirado em {formatDate(company.lastTrialEndDate)}
                      </Text>
                    )}
                    {company.subscriptionStatus === 'inativa' && company.lastPaidEndDate && (
                      <Text style={styles.listSubtitle}>
                        Assinatura expirada em {formatDate(company.lastPaidEndDate)}
                      </Text>
                    )}
                  </View>
                  <View style={styles.subscriptionMeta}>
                    <StatusPill
                      label={resolveSubscriptionLabel(company.subscriptionStatus)}
                      tone={company.subscriptionStatus === 'ativa' ? 'success' : company.subscriptionStatus === 'trial' ? 'warning' : 'neutral'}
                    />
                    <Text style={styles.listSubtitle}>{company.billingProvider}</Text>
                  </View>
                </View>
              ))
            )}
          </SectionCard>
        </View>
      )}

      {activeTab === 'devops' && (
        <View style={styles.tabContent}>
          <SectionCard title="Provisionar template de motos">
            <Text style={styles.listSubtitle}>
              Selecione as empresas para criar o template \"Motos\" com marcações padrão.
            </Text>
            {sortedCompanies.length === 0 ? (
              <Text style={styles.emptyText}>Nenhuma empresa encontrada.</Text>
            ) : (
              <View style={{ marginTop: theme.spacing.sm }}>
                {sortedCompanies.map(company => {
                  const isSelected = selectedMotoCompanies.includes(company.id);
                  return (
                    <TouchableOpacity
                      key={`moto-${company.id}`}
                      style={[styles.selectionRow, isSelected && styles.selectionRowActive]}
                      onPress={() => toggleMotoCompany(company.id)}
                    >
                      <MaterialCommunityIcons
                        name={isSelected ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
                        size={20}
                        color={isSelected ? theme.colors.primary.default : theme.colors.text.tertiary}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                        <Text style={styles.listSubtitle}>Plano: {company.planName}</Text>
                      </View>
                      <StatusPill
                        label={resolveSubscriptionLabel(company.subscriptionStatus)}
                        tone={company.subscriptionStatus === 'ativa' ? 'success' : company.subscriptionStatus === 'trial' ? 'warning' : 'neutral'}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
            {provisionMotoMessage ? (
              <Text style={[styles.listSubtitle, { marginTop: theme.spacing.sm }]}>{provisionMotoMessage}</Text>
            ) : null}
            <TouchableOpacity
              style={[styles.primaryActionButton, (isProvisioningMoto || selectedMotoCompanies.length === 0) && styles.primaryActionButtonDisabled]}
              onPress={handleProvisionMotoTemplates}
              disabled={isProvisioningMoto || selectedMotoCompanies.length === 0}
            >
              {isProvisioningMoto ? (
                <ActivityIndicator color={theme.colors.text.inverse} />
              ) : (
                <MaterialCommunityIcons name="rocket-launch-outline" size={18} color={theme.colors.text.inverse} />
              )}
              <Text style={styles.primaryActionText}>
                Criar template de motos ({selectedMotoCompanies.length})
              </Text>
            </TouchableOpacity>
          </SectionCard>

          <SectionCard title="Top consumo de imagens">
            {topCompaniesByImages.length === 0 ? (
              <Text style={styles.emptyText}>Nenhum dado de imagens encontrado.</Text>
            ) : (
              topCompaniesByImages.map(company => (
                <View key={`${company.id}-images`} style={styles.listRow}>
                  <View>
                    <Text style={styles.listTitle}>{company.nome || 'Empresa sem nome'}</Text>
                    <Text style={styles.listSubtitle}>Imagens: {company.num_fotos ?? 0}</Text>
                  </View>
                  <Text style={styles.listMetric}>{company.num_fotos ?? 0}</Text>
                </View>
              ))
            )}
          </SectionCard>

          <SectionCard title="Observacoes sobre sync">
            <Text style={styles.listSubtitle}>
              Pendencias locais (pending_operations/pending_images) nao sao expostas no Supabase.
            </Text>
            <Text style={styles.listSubtitle}>
              Use os testes em /settings/developer para diagnosticos offline.
            </Text>
          </SectionCard>
        </View>
      )}
    </ScrollView>
  );
});

export default DevPanelScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  content: {
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.xl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  headerActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  title: {
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.primary,
  },
  subtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginTop: 4,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.background.primary,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  refreshText: {
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.semibold as any,
    fontSize: theme.fontSizes.sm,
  },
  secondaryButtonText: {
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.semibold as any,
    fontSize: theme.fontSizes.sm,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  statusText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
  },
  alertCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: `${theme.colors.util.error}12`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  alertText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    flex: 1,
  },
  tabRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.lg,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.tertiary,
  },
  tabButtonActive: {
    backgroundColor: theme.colors.primary.default,
  },
  tabLabel: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  tabLabelActive: {
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.semibold as any,
  },
  tabContent: {
    gap: theme.spacing.lg,
  },
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  kpiCard: {
    flexGrow: 1,
    flexBasis: '45%',
    minWidth: 160,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
  },
  kpiTitle: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  kpiValue: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xxl,
    fontWeight: theme.fontWeights.bold as any,
    marginTop: theme.spacing.xs,
  },
  kpiSubtitle: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
    marginTop: 2,
  },
  sectionCard: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    ...theme.shadows.sm,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  emptyText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.tertiary,
  },
  listRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  listTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  listSubtitle: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: 2,
  },
  listMetric: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
  companyCard: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  companyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  companyMeta: {
    gap: 4,
  },
  subscriptionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  subscriptionMeta: {
    alignItems: 'flex-end',
    gap: 4,
  },
  selectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  selectionRowActive: {
    backgroundColor: `${theme.colors.primary.light}18`,
  },
  primaryActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary.default,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.md,
  },
  primaryActionButtonDisabled: {
    opacity: 0.6,
  },
  primaryActionText: {
    color: theme.colors.text.inverse,
    fontWeight: theme.fontWeights.semibold as any,
    fontSize: theme.fontSizes.sm,
  },
  pill: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.pill,
    borderWidth: 1,
  },
  pillText: {
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.semibold as any,
  },
});
